import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import AdminSidebar from '../../components/AdminSidebar';
import ListingFilters from './components/ListingFilters';
import ListingTable from './components/ListingTable';
import ListingDetailPanel from './components/ListingDetailPanel';
import { List } from 'lucide-react';

const AdminListingsModerationDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('svi');
  const [selectedListing, setSelectedListing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase?.from('marketplace_wishes')?.select(`
          id, wish_title, voucher_name, description, special_conditions,
          wish_type, max_price, currency, is_active, is_archived, is_visible,
          edit_note, created_at, updated_at, user_id,
          seller:user_profiles!user_id(id, full_name, email)
        `)?.order('created_at', { ascending: false });

      if (statusFilter === 'aktivni') {
        query = query?.eq('is_archived', false);
      } else if (statusFilter === 'arhivirani') {
        query = query?.eq('is_archived', true);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;

      const mapped = (data || [])?.map((w) => ({
        id: w?.id,
        wishTitle: w?.wish_title,
        voucherName: w?.voucher_name,
        description: w?.description,
        specialConditions: w?.special_conditions,
        wishType: w?.wish_type,
        maxPrice: w?.max_price,
        currency: w?.currency,
        isActive: w?.is_active,
        isArchived: w?.is_archived ?? false,
        isVisible: w?.is_visible ?? true,
        editNote: w?.edit_note,
        createdAt: w?.created_at,
        updatedAt: w?.updated_at,
        userId: w?.user_id,
        sellerName: w?.seller?.full_name || 'N/A',
        sellerEmail: w?.seller?.email || '',
      }));

      setListings(mapped);
    } catch (err) {
      setError(err?.message || 'Greška pri učitavanju oglasa');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const filteredListings = listings?.filter((l) => {
    if (!search) return true;
    const q = search?.toLowerCase();
    return (l?.wishTitle?.toLowerCase()?.includes(q) ||
    l?.voucherName?.toLowerCase()?.includes(q) ||
    l?.sellerName?.toLowerCase()?.includes(q) ||
    l?.sellerEmail?.toLowerCase()?.includes(q) || l?.description?.toLowerCase()?.includes(q));
  });

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleArchive = async (id) => {
    setSaving(true);
    try {
      const { error: updateError } = await supabase?.from('marketplace_wishes')?.update({ is_archived: true, is_visible: false, archived_at: new Date()?.toISOString() })?.eq('id', id);
      if (updateError) throw updateError;
      await fetchListings();
      setSelectedListing(null);
      showSuccess('Oglas je arhiviran');
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (id) => {
    setSaving(true);
    try {
      const { error: updateError } = await supabase?.from('marketplace_wishes')?.update({ is_archived: false, is_visible: true, archived_at: null })?.eq('id', id);
      if (updateError) throw updateError;
      await fetchListings();
      setSelectedListing(null);
      showSuccess('Oglas je vraćen');
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (id, updates) => {
    setSaving(true);
    try {
      const { error: updateError } = await supabase?.from('marketplace_wishes')?.update({ ...updates, updated_at: new Date()?.toISOString() })?.eq('id', id);
      if (updateError) throw updateError;
      await fetchListings();
      showSuccess('Oglas je izmenjen');
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <AdminSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-64'}`}>
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <List className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Moderacija Oglasa</h1>
              <p className="text-sm text-gray-500">Pregled i upravljanje oglasima na platformi</p>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
              <button onClick={() => setError(null)} className="ml-2 font-medium underline">Zatvori</button>
            </div>
          )}
          {successMsg && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              {successMsg}
            </div>
          )}

          {/* Main content */}
          <div className="flex gap-6">
            {/* Left: Table */}
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <ListingFilters
                  search={search}
                  onSearchChange={setSearch}
                  statusFilter={statusFilter}
                  onStatusChange={setStatusFilter}
                />
                <p className="text-xs text-gray-500">{filteredListings?.length} oglasa</p>
              </div>
              <ListingTable
                listings={filteredListings}
                selectedId={selectedListing?.id}
                onSelect={setSelectedListing}
                loading={loading}
              />
            </div>

            {/* Right: Detail panel */}
            {selectedListing && (
              <div className="w-96 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex-shrink-0">
                <ListingDetailPanel
                  listing={selectedListing}
                  onClose={() => setSelectedListing(null)}
                  onArchive={handleArchive}
                  onRestore={handleRestore}
                  onSaveEdit={handleSaveEdit}
                  saving={saving}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminListingsModerationDashboard;
